import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { FormField, Input, Select, Textarea } from '../../components/ui/FormField.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { useBackNavigation } from '../../hooks/useBackNavigation.js';
import { useSaveMutation } from '../../hooks/useSaveMutation.js';
import { api } from '../../services/api.js';
import { fetchAllPages } from '../../services/fetchAllPages.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';

interface Car {
  _id: string;
  name?: string;
}

interface MaintenanceData {
  car_id?: string;
  title?: string;
  maintenance_type?: string;
  status?: string;
  start_at?: string;
  end_at?: string;
  odometer_km?: number | string;
  cost_amount_minor?: number | string;
  cost_currency_code?: string;
  notes?: string;
}

// datetime-local wants 'YYYY-MM-DDTHH:mm'; the API returns ISO — trim to minutes.
function toLocal(v?: string) {
  return v ? v.slice(0, 16) : '';
}
function toIso(v: string) {
  return v ? new Date(v).toISOString() : undefined;
}

export function MaintenanceFormPage() {
  const navigate = useNavigate();
  const goBack = useBackNavigation('/maintenance');
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form, setForm] = useState<Record<string, string>>({
    car_id: '',
    title: '',
    maintenance_type: 'scheduled',
    status: 'planned',
    start_at: '',
    end_at: '',
    odometer_km: '',
    cost_amount: '',
    notes: '',
  });
  const [error, setError] = useState('');

  const { data: cars } = useQuery({
    queryKey: RESOURCE_KEYS.car.list,
    // 162 (US3): every car, not the first page of 20. `/cars` paginates with a default of 20 and
    // a maximum of 100, and this control read it with no page parameter at all — so on a fleet
    // over 20 cars the car you wanted was simply not in the list, with no error and no note.
    queryFn: () => fetchAllPages<Car>('/cars', (url) => api.get(url)),
  });

  const { data: existing } = useQuery({
    queryKey: RESOURCE_KEYS.maintenance.one(id as string),
    queryFn: () => api.get<MaintenanceData>(`/maintenance-records/${id}`),
    enabled: isEdit,
  });

  useEffect(() => {
    if (!existing) return;
    setForm((f) => ({
      ...f,
      car_id: existing.car_id ?? '',
      title: existing.title ?? '',
      maintenance_type: existing.maintenance_type ?? 'scheduled',
      status: existing.status ?? 'planned',
      start_at: toLocal(existing.start_at),
      end_at: toLocal(existing.end_at),
      odometer_km: existing.odometer_km != null ? String(existing.odometer_km) : '',
      cost_amount:
        existing.cost_amount_minor != null
          ? (Number(existing.cost_amount_minor) / 100).toFixed(2)
          : '',
      notes: existing.notes ?? '',
    }));
  }, [existing]);

  function buildPayload(): Record<string, unknown> {
    const out: Record<string, unknown> = {
      title: form.title,
      maintenance_type: form.maintenance_type,
      status: form.status,
    };
    if (!isEdit) out.car_id = form.car_id;
    out.start_at = form.start_at ? toIso(form.start_at) : null;
    out.end_at = form.end_at ? toIso(form.end_at) : null;
    if (form.odometer_km) out.odometer_km = Number(form.odometer_km);
    if (form.cost_amount) {
      out.cost_amount_minor = Math.round(Number(form.cost_amount) * 100);
      out.cost_currency_code = 'MYR';
    }
    if (form.notes) out.notes = form.notes;
    return out;
  }

  // 161 (US2): this form invalidated nothing, so the maintenance list kept the old row after a save.
  const { save, isPending } = useSaveMutation({
    resource: 'maintenance',
    id,
    mutationFn: () =>
      isEdit
        ? api.patch(`/maintenance-records/${id}`, buildPayload())
        : api.post('/maintenance-records', buildPayload()),
    onSaved: () => navigate('/maintenance'),
    onError: (err) => setError((err as { message?: string }).message ?? 'Failed to save record'),
  });

  function set(k: string, v: string) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  return (
    <div>
      <PageHeader
        title={isEdit ? 'Edit Maintenance' : 'New Maintenance'}
        description="A planned/in-progress record with a window blocks the car from bookings."
        fallback="/maintenance"
      />
      <form
        className="max-w-2xl space-y-6"
        onSubmit={(e) => {
          e.preventDefault();
          setError('');
          void save();
        }}
      >
        <Card>
          <div className="space-y-4">
            {!isEdit && (
              <FormField label="Car" name="car_id" required>
                <Select
                  id="car_id"
                  value={form.car_id}
                  onChange={(e) => set('car_id', (e.target as HTMLSelectElement).value)}
                  required
                >
                  <option value="" disabled>
                    Select a car…
                  </option>
                  {cars?.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.name ?? c._id}
                    </option>
                  ))}
                </Select>
              </FormField>
            )}
            <FormField label="Title" name="title" required>
              <Input
                id="title"
                value={form.title}
                onInput={(e) => set('title', (e.target as HTMLInputElement).value)}
                required
              />
            </FormField>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField label="Type" name="maintenance_type" required>
                <Select
                  id="maintenance_type"
                  value={form.maintenance_type}
                  onChange={(e) => set('maintenance_type', (e.target as HTMLSelectElement).value)}
                >
                  <option value="scheduled">Scheduled</option>
                  <option value="repair">Repair</option>
                  <option value="inspection">Inspection</option>
                  <option value="cleaning">Cleaning</option>
                  <option value="other">Other</option>
                </Select>
              </FormField>
              <FormField label="Status" name="status" required>
                <Select
                  id="status"
                  value={form.status}
                  onChange={(e) => set('status', (e.target as HTMLSelectElement).value)}
                >
                  <option value="planned">Planned</option>
                  <option value="in_progress">In progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </Select>
              </FormField>
              <FormField label="Window start" name="start_at" hint="Blocks the car when set">
                <Input
                  id="start_at"
                  type="datetime-local"
                  value={form.start_at}
                  onInput={(e) => set('start_at', (e.target as HTMLInputElement).value)}
                />
              </FormField>
              <FormField label="Window end" name="end_at">
                <Input
                  id="end_at"
                  type="datetime-local"
                  value={form.end_at}
                  onInput={(e) => set('end_at', (e.target as HTMLInputElement).value)}
                />
              </FormField>
              <FormField label="Odometer (km)" name="odometer_km">
                <Input
                  id="odometer_km"
                  type="number"
                  min={0}
                  value={form.odometer_km}
                  onInput={(e) => set('odometer_km', (e.target as HTMLInputElement).value)}
                />
              </FormField>
              <FormField label="Cost (RM)" name="cost_amount">
                <Input
                  id="cost_amount"
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.cost_amount}
                  onInput={(e) => set('cost_amount', (e.target as HTMLInputElement).value)}
                />
              </FormField>
            </div>
            <FormField label="Notes" name="notes">
              <Textarea
                id="notes"
                rows={2}
                value={form.notes}
                onInput={(e) => set('notes', (e.target as HTMLTextAreaElement).value)}
              />
            </FormField>
          </div>
        </Card>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex gap-3">
          <Button type="submit" loading={isPending}>
            {isEdit ? 'Save Changes' : 'Create Record'}
          </Button>
          <Button type="button" variant="ghost" onClick={goBack}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
